import { put, call, delay as delaySaga } from 'redux-saga/effects';
import identity from 'lodash/identity';
import noop from 'lodash/noop';
import assert from '../assert';
import pipe from '../pipe';
const isFunc = f => typeof f === 'function';
const makeEffect = f => payload => isFunc(f) ? call(f, payload) : f;
const makeMapper = map => isFunc(map) ? map : () => map;
const DEFAULT_DELAY = 1000;

/**
 * Abstraction over effect which may throw when yielded
 * Inspired by Maybe monad
 * TODO: allow use fetch, spawn runner effect
 */
class MaybeWorker {
  constructor(maybeEffect) {
    this.maybeEffect = maybeEffect;
    this.someWorker = noop;
    this.noneWorker = noop;
    this.finallyWorker = noop;
  }
  getMaybeEffect() {
    if (this.retryOptions) {
      const {
        times,
        when,
        whenErrorCode,
        alwaysRetry,
        delay = DEFAULT_DELAY,
        exponential
      } = this.retryOptions;
      return call([this,
      // binds context of this class to function below
      function* () {
        let triesCount = 0;
        while (true) {
          triesCount++;
          try {
            return yield this.maybeEffect;
          } catch (error) {
            const retry = triesCount < times && (alwaysRetry || whenErrorCode && whenErrorCode === error.errorCode || (when == null ? void 0 : when(error)));
            if (retry) {
              yield delaySaga(exponential ? (2 ** triesCount - 1) * delay // exponential backoff
              : delay);
            } else {
              throw error;
            }
          }
        }
      }]);
    }
    return this.maybeEffect;
  }

  /**
   * Configures retry options
   */
  retry({
    times,
    when,
    whenErrorCode,
    exponential,
    delay = DEFAULT_DELAY
  }) {
    if (process.env.NODE_ENV !== 'production') {
      assert(typeof times === 'number' && times > 0, 'options.times must be an integer greater then zero');
      assert(typeof delay === 'number' && delay > 0, 'options.delay must be an integer greater then zero');
      assert(typeof when === 'undefined' || typeof when === 'function', 'options.when must be a function');
      assert(typeof whenErrorCode === 'undefined' || typeof whenErrorCode === 'string' && whenErrorCode !== '', 'options.whenErrorCode must be non-empty string');
    }
    const alwaysRetry = typeof when === 'undefined' && typeof whenErrorCode === 'undefined';
    this.retryOptions = {
      times,
      when,
      whenErrorCode,
      delay,
      alwaysRetry,
      exponential
    };
    return this;
  }

  /**
   * Perform action if Maybe returned Some result
   * You can pass a function, generator or saga effect
   */
  some(worker) {
    this.someWorker = makeEffect(worker);
    return this;
  }

  /**
   * Perform action if Maybe returned None result
   * You can pass a function, generator or saga effect
   */
  none(worker) {
    this.noneWorker = makeEffect(worker);
    return this;
  }

  /**
   * Perform an action in the end regardless of result
   * You can pass a function, generator or saga effect
   */
  finally(worker) {
    this.finallyWorker = makeEffect(worker);
    return this;
  }

  /**
   * Bind redux saga routine.
   * Will call routine.success if Maybe has some value
   * of routine.failure if Maybe has None value.
   */
  bind(routine) {
    if (process.env.NODE_ENV !== 'production') {
      assert(['request', 'success', 'failure', 'fulfill'].every(t => isFunc(routine[t])), 'Expected a valid redux saga routine');
    }
    this.routine = routine;
    this.successMapper = this.successMapper || identity;
    this.failureMapper = this.failureMapper || identity;
    return this;
  }

  /**
   * Provide some value mapper which will be passed to routine.success
   * @param {any} map mapping function which acceps Some argument or any value
   */
  mapSuccess(map) {
    this.successMapper = makeMapper(map);
    return this;
  }

  /**
   * Provide none value mapper which will be passed to routine.failure
   * @param {any} map mapping function which acceps None argument or any value
   */
  mapFailure(map) {
    this.failureMapper = makeMapper(map);
    return this;
  }

  /**
   * Returns create redux saga call effect
   */
  run() {
    const dispatchRoutines = !!this.routine;

    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const runner = function* () {
      if (dispatchRoutines) {
        yield put(this.routine.request());
      }
      try {
        const some = yield this.getMaybeEffect();
        if (dispatchRoutines) {
          yield pipe(this.successMapper, this.routine.success, put)(some);
        }
        yield this.someWorker(some);
        if (dispatchRoutines) {
          yield pipe(this.successMapper, this.routine.fulfill, put)(some);
        }
        return {
          some
        };
      } catch (error) {
        if (process.env.NODE_ENV === 'production') {
          console.error(error, error.message); // eslint-disable-line no-console
        }
        if (dispatchRoutines) {
          yield pipe(this.failureMapper, this.routine.failure, put)(error);
        }
        yield this.noneWorker(error);
        if (dispatchRoutines) {
          yield pipe(this.failureMapper, this.routine.fulfill, put)(error);
        }
        return {
          none: error
        };
      } finally {
        yield this.finallyWorker();
      }
    };
    /* eslint-enable @typescript-eslint/no-non-null-assertion */

    return call([this, runner]);
  }
}
export default MaybeWorker;